import { type FC, type ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useRouteLoaderData } from '@remix-run/react';
import classNames from 'classnames';

import { GATE_SCOPES, type GateScope } from '~/constants/community';
import { ImpressionSuppressionContext } from '~/context/ImpressionSuppressionContext';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { loader as communityLoader } from '~/routes/community';
import { NectarPageBlock, nectarPageBlock } from '~/utils/nectarPageBlock';

import { PillButton } from './ui/PillButton';

import { useCommunityAccess } from '../_hooks/useCommunityAccess';
import { useCommunityJoinGate } from '../_hooks/useCommunityJoinGate';
import { useCommunityMembership } from '../_hooks/useCommunityMembership';
import { useOpenAnswerQuestionModal } from '../_hooks/useOpenAnswerQuestionModal';
import { useOpenChannelUpgradeModal } from '../_hooks/useOpenChannelUpgradeModal';
import { type SpaceAccessTarget, useSpaceAccess } from '../_hooks/useSpaceAccess';
import { useSpacePurchasableTiers } from '../_hooks/useSpacePurchasableTiers';
import { isConditionLock } from '../_utils/communityAccess';
import { ClosedCommunityBanner } from './ClosedCommunityBanner';
import { JoinBanner } from './JoinBanner';
import { JoinRefusedBanner } from './JoinRefusedBanner';
import { OwnerGateButton } from './OwnerGateButton';
import { OwnerMessageCard } from './OwnerMessageCard';
import { UpgradeBanner } from './UpgradeBanner';

const GATE_DESCRIPTION_KEYS: Record<GateScope, string> = {
  [GATE_SCOPES.Post]: 'community.paid_gate.post_description',
  [GATE_SCOPES.Channel]: 'community.paid_gate.channel_description',
  [GATE_SCOPES.Community]: 'community.paid_gate.community_description',
};

interface GatedFeedOverlayProps {
  children: ReactNode;
  scope: GateScope;
  space?: SpaceAccessTarget | null;
}

export const GatedFeedOverlay: FC<GatedFeedOverlayProps> = ({ children, scope, space }) => {
  const { t } = useTranslation();
  const { isMember, openJoinModal, openSignInModal } = useCommunityMembership();
  const { isCommunityAccessible } = useCommunityAccess();
  const isLoggedIn = useIsLoggedIn();
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const openChannelUpgradeModal = useOpenChannelUpgradeModal();
  const openAnswerQuestionModal = useOpenAnswerQuestionModal();
  const { isSpaceClosed, isSpaceConditionLocked, lapsedSubscriptionFor, profileFieldIdFor } = useSpaceAccess();
  const [subscribedSpaceId, setSubscribedSpaceId] = useState<string>();
  const community = layoutData?.community ?? null;
  const showsRealPosts = !isMember && isCommunityAccessible;

  const isChannelGate = scope !== GATE_SCOPES.Community && space != null;
  const showsOwnerGate = isChannelGate && isSpaceConditionLocked(space);
  const isChannelClosed = isChannelGate && isSpaceClosed(space);
  const channel = layoutData?.spaces?.find((layoutSpace) => layoutSpace.id === space?.id);
  const channelName = channel?.name ?? t('community.space_name_fallback');
  const lapsedSubscription = isChannelGate ? lapsedSubscriptionFor(space) : undefined;
  const listName = channel?.gate?.subscribable_list_name;
  const ownerCard =
    showsOwnerGate && profileFieldIdFor(space) != null
      ? {
          message: t('community.answer_question.locked_message'),
          action: (
            <PillButton
              type="button"
              variant="primary"
              className="mt-6"
              onClick={() => openAnswerQuestionModal(space.id)}
            >
              {t('community.answer_question.locked_cta')}
            </PillButton>
          ),
        }
      : {
          message: channel?.gate?.message ?? t('community.owner_gate.default_message'),
          note:
            lapsedSubscription === 'list' && listName && subscribedSpaceId !== space?.id
              ? t('community.list_gate.former_member', { listName })
              : undefined,
          action: (
            <OwnerGateButton
              gate={channel?.gate}
              spaceSlug={channel?.slug}
              onSubscribed={() => setSubscribedSpaceId(space?.id)}
              className="mt-6"
            />
          ),
        };

  // Warms the pricing cache while the gate is on screen (community-scope CTAs open
  // the upgrade modal) and feeds the gate. Disabled for channel/post scopes,
  // where the optimistic default keeps the classification at 'upgrade' — the closed
  // state is a community-scope concept for now (space gates are out of scope).
  const gate = useCommunityJoinGate({ enabled: scope === GATE_SCOPES.Community });

  // Channel/post gates sell in place; the CTA waits for the catalog so it never appears and then vanishes.
  const { hasPurchasableTier: hasChannelPurchasableTier, isPending: isChannelPricingPending } =
    useSpacePurchasableTiers({
      spaceId: space?.id,
      enabled: isChannelGate && !isChannelClosed && !showsOwnerGate,
    });
  const sellsChannelTier = !isChannelPricingPending && hasChannelPurchasableTier;
  const requiresUpgrade = !isMember && isLoggedIn && gate === 'upgrade';
  // Unlike the upgrade path, guests see the closed banner too: signing up can't
  // grant access to a community with nothing to sell.
  const isClosedForViewer = !isMember && gate === 'closed';
  // Visitors who don't meet the community's conditions see the owner's screen on any gate. Members see it on the
  // community's gate only, because a channel gate shows that channel's own lock.
  const seesCommunityRefusal = !isMember || !isChannelGate;
  const isRefused = gate === 'refused' && seesCommunityRefusal;
  // A guest's data can't be checked before they sign in, so they see the owner's message with a way to join.
  const isGuestOnConditionGate = !isLoggedIn && isConditionLock(community?.lock_reason);
  const growsWithPosts = isMember || requiresUpgrade || isClosedForViewer || isRefused || isGuestOnConditionGate;

  return (
    <div className={classNames('relative', growsWithPosts ? 'min-h-[400px]' : 'flex-1 min-h-0 overflow-hidden')}>
      {/* Posts stay mounted (blurred + inert) behind the gate, so suppress their
          impressions — the viewer can't actually read them. */}
      <ImpressionSuppressionContext.Provider value={true}>
        <div
          inert
          aria-hidden="true"
          className={classNames('flex flex-col gap-6 select-none', showsRealPosts ? 'blur-[1.5px]' : 'blur-[4px]')}
        >
          {children}
        </div>
      </ImpressionSuppressionContext.Provider>
      <div
        className={classNames(
          'absolute inset-x-0 flex justify-center px-4',
          growsWithPosts ? 'top-6' : 'top-1/2 -translate-y-1/2',
        )}
      >
        {isRefused ? (
          <div className="w-full max-w-[480px]">
            <JoinRefusedBanner />
          </div>
        ) : isMember && showsOwnerGate ? (
          <div className="w-full max-w-[480px]">
            <OwnerMessageCard title={channelName} {...ownerCard} />
          </div>
        ) : isMember ? (
          <div className="flex w-full max-w-[440px] flex-col items-center justify-center rounded-3xl bg-white px-6 py-12 text-center shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]">
            <div className="flex size-12 items-center justify-center rounded-full bg-black/5">
              <LockClosedIcon aria-hidden="true" className="size-5 text-black/60" />
            </div>
            <h2 className="mt-4 text-xl font-semibold leading-7 tracking-[-0.4px] text-black">
              {t('community.paid_gate.title')}
            </h2>
            <p className="mt-2 max-w-md text-sm leading-5 text-black/60">
              {isChannelClosed ? (
                <Trans
                  i18nKey="community.paid_gate.channel_closed_description"
                  values={{ channelName }}
                  components={{ strong: <strong className="font-semibold text-black" /> }}
                />
              ) : sellsChannelTier && lapsedSubscription === 'paid' ? (
                t('community.paid_gate.subscription_ended')
              ) : (
                t(GATE_DESCRIPTION_KEYS[scope])
              )}
            </p>
            {isChannelClosed ? null : scope === GATE_SCOPES.Community || !space ? (
              // No space means the community itself is the gate (e.g. GatedCommunityOverlay
              // never passes one), regardless of the scope it was asked to describe.
              <PillButton
                type="button"
                onClick={() => openJoinModal(NectarPageBlock.CommunityFeedUpgradeButton)}
                variant="primary"
                className="mt-6"
                {...nectarPageBlock(NectarPageBlock.CommunityFeedUpgradeButton)}
              >
                {t('community.paid_gate.cta')}
              </PillButton>
            ) : (
              sellsChannelTier && (
                <PillButton
                  type="button"
                  onClick={() => openChannelUpgradeModal(space.id, NectarPageBlock.CommunityFeedUpgradeButton)}
                  variant="primary"
                  className="mt-6"
                  {...nectarPageBlock(NectarPageBlock.CommunityFeedUpgradeButton)}
                >
                  {t('community.paid_gate.cta')}
                </PillButton>
              )
            )}
          </div>
        ) : requiresUpgrade ? (
          <UpgradeBanner onUpgrade={() => openJoinModal(NectarPageBlock.CommunityFeedUpgradeButton)} />
        ) : isClosedForViewer ? (
          <div className="w-full max-w-[480px]">
            <ClosedCommunityBanner onSignIn={isLoggedIn ? undefined : openSignInModal} />
          </div>
        ) : isGuestOnConditionGate ? (
          <div className="w-full max-w-[480px]">
            <JoinRefusedBanner onJoin={() => openJoinModal(NectarPageBlock.CommunityJoinBanner)} />
          </div>
        ) : (
          <div className="w-full max-w-[480px]">
            <JoinBanner community={community} onJoin={() => openJoinModal(NectarPageBlock.CommunityJoinBanner)} />
          </div>
        )}
      </div>
    </div>
  );
};
